import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from './ui/notify';
import { apiCall } from './config';

function WorkInstructions({ user }) {
  const [instructions, setInstructions] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [activeTab, setActiveTab] = useState('work'); // 'work' or 'employee'
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    workInstructions: '',
    employeeInfo: ''
  });

  useEffect(() => {
    loadInstructions();
  }, []);

  const loadInstructions = async () => {
    try {
      const response = await apiCall('/work-instructions');
      // apiCall gibt bei Erfolg direkt das JSON zurück
      if (response && (response.workInstructions !== undefined || response.employeeInfo !== undefined)) {
        setInstructions(response);
        setEditData({
          workInstructions: response.workInstructions || '',
          employeeInfo: response.employeeInfo || ''
        });
      } else {
        // Wenn keine Daten vorhanden sind, verwende Standard-Inhalte
        setDefaultInstructions();
      }
    } catch (error) {
      console.error('Fehler beim Laden der Arbeitsanweisungen:', error);
      // Fallback zu Standard-Inhalten
      setDefaultInstructions();
    }
  };

  const setDefaultInstructions = () => {
    const defaultData = {
      workInstructions: `
## Allgemeine Arbeitsregeln

### Arbeitszeiten & Pünktlichkeit
- **Pünktlich zu Schichtbeginn** bei der eingetragenen Gepäckstation sein
- Bei Verspätungen: **017643831566** (Einsatzleitung) anrufen
- Wochentags 20 min vor 10 Uhr da sein (wird vergütet)
- **Stundenzettel** vor Ort ausfüllen bei Schichtbeginn/-ende

### Arbeitsplatz & Ausrüstung
- **Warm anziehen!** (Außenarbeitsplatz)
- Snackpaket pro Schicht (bei Bedarf eigenes Essen mitbringen)
- Bei Stationswechsel: Aus- und wieder eintrags in Stundenzettel

### Arbeitsweise Gepäckannahme
1. **Gepäckzettel verkaufen** und Wertmarke abreißen
2. **Gepäckzettel in 2 Teilen:**
   - Abholschein für Kunde
   - Zweiter Teil mit Wäscheklammer am Gepäck befestigen
3. **Gepäck an Haken** mit gleicher Nummer hängen
4. **Sperrgepäck** (Kinderwagen etc.): Eigene Nummerierung verwenden

### Arbeitsweise Gepäckabholung
1. **Abholschein prüfen** und Gepäck am entsprechenden Haken suchen
2. **Beide Zettel zusammenpinnen** und zur Annahme zurücklegen
3. **Ohne Abholschein:** Kunde muss Inhalt beschreiben + Formular ausfüllen
4. **Vortags-Gepäck:** Nachzahlung erforderlich
5. **Tägliche Farbwechsel** der Abholscheine beachten

### Arbeitsende
1. **Stundenzettel** vollständig ausfüllen
2. **Trinkgeld aufteilen** (separate Box bei Kassenkraft)
3. Bei frühem Ende: **Einsatzleitung informieren**
`,
      employeeInfo: `
## Wichtige Infos für Mitarbeiter

### Trinkgeld-System 💰
- **Pro Station eigene Trinkgeldbox** organisieren (Becher/Sparschwein)
- Wird am Schichtende unter allen Mitarbeitern **aufgeteilt**
- Separate Sammlung durch Kassenkraft

### Rollstuhl-Service ♿
- **G1 und G10:** Je 2 Rollstühle kostenlos
- **Nur gegen amtlichen Lichtbildausweis** als Pfand
- **Reservierungen haben Vorrang**

### Pausen & Flexibilität
- **Pausen im Team absprechen** - nie alle gleichzeitig
- Pausen sind **vergütet** - auf Station bleiben
- **Einsatzbereitschaft** auch in Pause

### Station finden 🗺️
- **G1 = Eingang P1, G5 = Eingang P5** usw.
- Bei Problemen: **Sicherheitsdienst fragen**
- Übersichtsplan vor Ort verfügbar

### Besondere Situationen
- **Früher Feierabend möglich** wenn alle Gepäckstücke abgeholt
- **Andere Station helfen** bei frühem Ende
- **Verspätung:** Sofort Einsatzleitung anrufen

### Service-Philosophie 🤝
**Gepäckstationen sind Teil des Sicherheitskonzeptes**
- Freundlich und hilfsbereit sein
- Essenzielle Serviceleistungen erbringen
- **Oktoberfest-Gäste** bestmöglich betreuen

### Notfall-Kontakt
📞 **017643831566** (Einsatzleitung)
`
    };
    setInstructions(defaultData);
    setEditData(defaultData);
  };

  const saveInstructions = async () => {
    try {
      const response = await apiCall('/work-instructions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editData),
      });
      
      // apiCall gibt bei Erfolg direkt das JSON zurück
      if (response && response.success) {
        setInstructions(editData);
        setIsEditing(false);
        notify('Arbeitsanweisungen erfolgreich gespeichert!');
      } else {
        throw new Error(response.message || 'Unbekannter Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      notify('Fehler beim Speichern der Arbeitsanweisungen: ' + error.message);
    }
  };

  const formatText = (text) => {
    if (!text) return '';
    
    return text
      .split('\n')
      .map((line, index) => {
        // Überschriften
        if (line.startsWith('## ')) {
          return <h4 key={index} className="text-primary fw-bold mt-4 mb-2">{line.replace('## ', '')}</h4>;
        }
        if (line.startsWith('### ')) {
          return <h5 key={index} className="text-secondary fw-bold mt-3 mb-2">{line.replace('### ', '')}</h5>;
        }
        
        // Listen
        if (line.startsWith('- ')) {
          const content = line.replace('- ', '');
          // Ersetze **text** mit bold
          const boldText = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          return (
            <li key={index} className="mb-1" dangerouslySetInnerHTML={{ __html: boldText }} />
          );
        }
        
        // Nummerierte Listen
        if (line.match(/^\d+\./)) {
          const content = line.replace(/^\d+\.\s*/, '');
          const boldText = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          return (
            <li key={index} className="mb-1" dangerouslySetInnerHTML={{ __html: boldText }} />
          );
        }
        
        // Normale Zeilen
        if (line.trim()) {
          const boldText = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          return <p key={index} className="mb-2" dangerouslySetInnerHTML={{ __html: boldText }} />;
        }
        
        return null;
      })
      .filter(Boolean);
  };

  if (!isVisible) {
    return (
      <div className="card mb-4">
        <div className="card-body p-2">
          <button 
            className="btn btn-sm btn-outline-info"
            onClick={() => setIsVisible(true)}
          >
            <i className="bi bi-info-circle me-1"></i>
            Arbeitsanweisungen anzeigen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-4 border-info">
      <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <i className="bi bi-clipboard-check me-2"></i>
          <h5 className="mb-0">Arbeitsanweisungen & Mitarbeiterinfo</h5>
        </div>
        <div className="d-flex gap-2">
          {user.role === 'admin' && (
            <>
              {isEditing ? (
                <>
                  <button 
                    className="btn btn-sm btn-light"
                    onClick={saveInstructions}
                  >
                    <i className="bi bi-check-lg me-1"></i>
                    Speichern
                  </button>
                  <button 
                    className="btn btn-sm btn-outline-light"
                    onClick={() => {
                      setIsEditing(false);
                      setEditData({
                        workInstructions: instructions?.workInstructions || '',
                        employeeInfo: instructions?.employeeInfo || ''
                      });
                    }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </>
              ) : (
                <button 
                  className="btn btn-sm btn-light"
                  onClick={() => setIsEditing(true)}
                >
                  <i className="bi bi-pencil me-1"></i>
                  Bearbeiten
                </button>
              )}
            </>
          )}
          <button 
            className="btn btn-sm btn-outline-light"
            onClick={() => setIsVisible(false)}
          >
            <i className="bi bi-eye-slash"></i>
          </button>
        </div>
      </div>
      
      <div className="card-body">
        {/* Tabs */}
        <ul className="nav nav-pills mb-3">
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'work' ? 'active' : ''}`}
              onClick={() => setActiveTab('work')}
            >
              <i className="bi bi-gear me-1"></i>
              Wichtig für die Arbeit
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'employee' ? 'active' : ''}`}
              onClick={() => setActiveTab('employee')}
            >
              <i className="bi bi-person-check me-1"></i>
              Wichtig für Mitarbeiter
            </button>
          </li>
        </ul>

        {/* Content */}
        <div className="tab-content">
          {isEditing ? (
            <div className="row">
              <div className="col-md-6">
                <h6>Wichtig für die Arbeit</h6>
                <textarea
                  className="form-control"
                  rows="20"
                  value={editData.workInstructions}
                  onChange={(e) => setEditData({...editData, workInstructions: e.target.value})}
                  placeholder="Markdown-Format unterstützt: ## Überschrift, ### Unterüberschrift, - Listenpunkt, **fett**"
                />
              </div>
              <div className="col-md-6">
                <h6>Wichtig für Mitarbeiter</h6>
                <textarea
                  className="form-control"
                  rows="20"
                  value={editData.employeeInfo}
                  onChange={(e) => setEditData({...editData, employeeInfo: e.target.value})}
                  placeholder="Markdown-Format unterstützt: ## Überschrift, ### Unterüberschrift, - Listenpunkt, **fett**"
                />
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'work' && (
                <div className="instructions-content">
                  {instructions?.workInstructions ? (
                    formatText(instructions.workInstructions)
                  ) : (
                    <p className="text-muted">Keine Arbeitsanweisungen verfügbar.</p>
                  )}
                </div>
              )}
              
              {activeTab === 'employee' && (
                <div className="instructions-content">
                  {instructions?.employeeInfo ? (
                    formatText(instructions.employeeInfo)
                  ) : (
                    <p className="text-muted">Keine Mitarbeiterinformationen verfügbar.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default WorkInstructions;
